// src/utils/implantFilter.js
//
// Global "Implant" filter (superadmin only, not related to any shop).
//
// When implant mode is ON every GET request to /product/ or /order/ gets
// `?group=implant`, and category create/update requests are saved with
// group=implant. No page needs to be edited.
//
// IMPORTANT: import this file FIRST in src/index.js, before App, so that every
// axios.create() instance made by the pages is patched.

import axios from 'axios';

export const IMPLANT_KEY = 'implant_mode';

export const isImplantMode = () => {
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (user.type !== 'SUPER_ADMIN') return false; // only superadmin
    return localStorage.getItem(IMPLANT_KEY) === '1';
  } catch {
    return false;
  }
};

export const IMPLANT_EVENT = 'implant-mode-change';

export const setImplantMode = (on) => {
  try {
    if (on) localStorage.setItem(IMPLANT_KEY, '1');
    else localStorage.removeItem(IMPLANT_KEY);
  } catch {
    /* ignore */
  }
  // tell the layout to re-mount the current page (no full page reload)
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(IMPLANT_EVENT));
  }
};

const FILTERED = [
  '/product/',
  '/order/',
  '/inventory/api/manufacturing',
  'lot-tracking',
  '/inventory/api/notes',
];
const SKIPPED = ['notification', 'cities', 'address'];

const shouldFilter = (url = '') => {
  const u = String(url);
  return FILTERED.some((p) => u.includes(p)) && !SKIPPED.some((p) => u.includes(p));
};

const isCategoryWrite = (url = '') =>
  /categories\/(create|\d+\/update)\/?/.test(String(url));

const addInterceptor = (instance) => {
  if (!instance || instance.__implantPatched) return instance;
  instance.__implantPatched = true;

  instance.interceptors.request.use((config) => {
    if (!isImplantMode()) return config;

    const method = (config.method || 'get').toLowerCase();
    const url = config.url || '';

    if (method === 'get' && shouldFilter(url)) {
      config.params = { ...(config.params || {}), group: 'implant' };
    }

    if ((method === 'post' || method === 'put') && isCategoryWrite(url)) {
      if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
        if (!config.data.has('group')) config.data.append('group', 'implant');
      } else if (config.data && typeof config.data === 'object') {
        config.data = { group: 'implant', ...config.data };
      }
    }
    return config;
  });

  return instance;
};

// patch the default axios + every axios.create() made after this point
addInterceptor(axios);
const originalCreate = axios.create.bind(axios);
axios.create = (...args) => addInterceptor(originalCreate(...args));

// pages that use fetch() directly
if (typeof window !== 'undefined' && window.fetch && !window.fetch.__implantPatched) {
  const originalFetch = window.fetch.bind(window);
  const patchedFetch = (input, init) => {
    try {
      const method = ((init && init.method) || (input && input.method) || 'GET').toUpperCase();
      const rawUrl = typeof input === 'string' ? input : input && input.url;
      if (isImplantMode() && method === 'GET' && rawUrl && shouldFilter(rawUrl)) {
        const u = new URL(rawUrl, window.location.origin);
        u.searchParams.set('group', 'implant');
        input = typeof input === 'string' ? u.toString() : new Request(u.toString(), input);
      }
    } catch {
      /* fall through with the original request */
    }
    return originalFetch(input, init);
  };
  patchedFetch.__implantPatched = true;
  window.fetch = patchedFetch;
}